import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  ArrowLeft, Users, FileText, DollarSign, BarChart3, 
  Mail, Calendar, Building2, CheckCircle, Clock, AlertTriangle, Save
} from "lucide-react";
import { toast } from "sonner";
import BrandWordmark from "../components/BrandWordmark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pricing, setPricing] = useState({
    cwmp_price: 59,
    final_report_price: 49,
    bundle_price: 89,
    subscription_price: 99,
    addon_email_price: 15,
    addon_ocr_price: 19,
    free_cwmp_enabled: true
  });
  const [savingPricing, setSavingPricing] = useState(false);
  const [contactRequests, setContactRequests] = useState([]);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (activeTab === "overview") fetchOverview();
    if (activeTab === "users") fetchUsers();
    if (activeTab === "projects") fetchProjects();
    if (activeTab === "pricing") fetchPricing();
  }, [activeTab]);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/check-admin`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (!data.is_admin) {
        toast.error("Admin access required");
        navigate('/dashboard');
      }
      setLoading(false);
    } catch (error) {
      navigate('/dashboard');
    }
  };

  const fetchOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/overview`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/projects`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/pricing`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const updateUserTier = async (userId, tier) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ tier })
      });
      if (response.ok) {
        toast.success('User updated');
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handlePricingChange = (field, value) => {
    setPricing(prev => ({
      ...prev,
      [field]: field === 'free_cwmp_enabled' ? value : parseFloat(value) || 0
    }));
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(pricing)
      });
      if (response.ok) {
        toast.success('Pricing updated successfully');
      } else {
        throw new Error('Failed to update pricing');
      }
    } catch (error) {
      toast.error('Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
              <BrandWordmark size="md" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg bg-card border border-primary/20">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FileText className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <DollarSign className="w-4 h-4 mr-2" />
              Pricing
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {overview && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-primary/20">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-foreground">{overview.stats.total_users}</p>
                        <p className="text-sm text-muted-foreground">Total Users</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-primary/20">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-foreground">{overview.stats.total_projects}</p>
                        <p className="text-sm text-muted-foreground">Total Projects</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-primary/20">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-foreground">{overview.stats.paid_projects}</p>
                        <p className="text-sm text-muted-foreground">Paid Projects</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-primary/20">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-yellow-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-foreground">${overview.stats.total_revenue}</p>
                        <p className="text-sm text-muted-foreground">Revenue</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-lg text-foreground">Recent Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {overview.recent_users?.map((u, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded bg-card/50">
                            <div>
                              <p className="text-foreground font-medium">{u.name || u.email}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                            <Badge variant="outline">{u.tier || 'free'}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-lg text-foreground">Recent Projects</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {overview.recent_projects?.map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded bg-card/50">
                            <div>
                              <p className="text-foreground font-medium">{p.project_name}</p>
                              <p className="text-xs text-muted-foreground">{p.county}</p>
                            </div>
                            <Badge className={p.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {p.payment_status || 'pending'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground">All Users ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-3 text-foreground">Name</th>
                        <th className="text-left py-3 text-foreground">Email</th>
                        <th className="text-left py-3 text-foreground">Company</th>
                        <th className="text-left py-3 text-foreground">Tier</th>
                        <th className="text-left py-3 text-foreground">Role</th>
                        <th className="text-left py-3 text-foreground">Created</th>
                        <th className="text-left py-3 text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-primary/10">
                          <td className="py-3 text-foreground">{u.name || '-'}</td>
                          <td className="py-3 text-muted-foreground">{u.email}</td>
                          <td className="py-3 text-muted-foreground">{u.company || '-'}</td>
                          <td className="py-3">
                            <Badge variant="outline">{u.tier || 'free'}</Badge>
                          </td>
                          <td className="py-3">
                            {u.role === 'admin' && <Badge className="bg-red-500/20 text-red-400">Admin</Badge>}
                          </td>
                          <td className="py-3 text-muted-foreground text-xs">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3">
                            <select 
                              className="bg-input/50 border border-primary/30 rounded px-2 py-1 text-xs"
                              value={u.tier || 'free'}
                              onChange={(e) => updateUserTier(u.user_id, e.target.value)}
                            >
                              <option value="free">Free</option>
                              <option value="basic">Basic</option>
                              <option value="standard">Standard</option>
                              <option value="premium">Premium</option>
                              <option value="enterprise">Enterprise</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-foreground">All Projects ({projects.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-3 text-foreground">Project Name</th>
                        <th className="text-left py-3 text-foreground">County</th>
                        <th className="text-left py-3 text-foreground">Type</th>
                        <th className="text-left py-3 text-foreground">Sq Ft</th>
                        <th className="text-left py-3 text-foreground">Status</th>
                        <th className="text-left py-3 text-foreground">Payment</th>
                        <th className="text-left py-3 text-foreground">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((p) => (
                        <tr key={p.project_id} className="border-b border-primary/10">
                          <td className="py-3 text-foreground">{p.project_name}</td>
                          <td className="py-3 text-muted-foreground">{p.county}</td>
                          <td className="py-3">
                            <Badge variant="outline">{p.project_type}</Badge>
                          </td>
                          <td className="py-3 text-muted-foreground">{p.square_footage?.toLocaleString()}</td>
                          <td className="py-3">
                            <Badge variant="outline">{p.status}</Badge>
                          </td>
                          <td className="py-3">
                            <Badge className={p.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {p.payment_status || 'pending'}
                            </Badge>
                          </td>
                          <td className="py-3 text-muted-foreground text-xs">
                            {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <div className="space-y-6">
              {/* Main Products */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-foreground">Product Pricing</CardTitle>
                  <CardDescription>Set prices for reports and bundles</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-foreground">CWMP Report Price ($)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={pricing.cwmp_price}
                        onChange={(e) => handlePricingChange('cwmp_price', e.target.value)}
                        className="border-primary/30"
                        data-testid="cwmp-price-input"
                      />
                      <p className="text-xs text-muted-foreground">Construction Waste Management Plan</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Final Report Price ($)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={pricing.final_report_price}
                        onChange={(e) => handlePricingChange('final_report_price', e.target.value)}
                        className="border-primary/30"
                        data-testid="final-report-price-input"
                      />
                      <p className="text-xs text-muted-foreground">Post-construction verification report</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Bundle Price ($)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={pricing.bundle_price}
                        onChange={(e) => handlePricingChange('bundle_price', e.target.value)}
                        className="border-primary/30"
                        data-testid="bundle-price-input"
                      />
                      <p className="text-xs text-muted-foreground">CWMP + Final Report together</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subscription */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-foreground">Subscription Plan</CardTitle>
                  <CardDescription>Monthly unlimited plan for high-volume users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-foreground">Monthly Subscription Price ($)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={pricing.subscription_price}
                        onChange={(e) => handlePricingChange('subscription_price', e.target.value)}
                        className="border-primary/30"
                        data-testid="subscription-price-input"
                      />
                      <p className="text-xs text-muted-foreground">Unlimited reports per month</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Add-ons */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-foreground">Add-on Pricing</CardTitle>
                  <CardDescription>Optional extras for reports</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-foreground">Email Delivery Add-on ($)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={pricing.addon_email_price}
                        onChange={(e) => handlePricingChange('addon_email_price', e.target.value)}
                        className="border-primary/30"
                        data-testid="email-addon-price-input"
                      />
                      <p className="text-xs text-muted-foreground">Instant delivery to permit office</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">OCR Processing Add-on ($)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={pricing.addon_ocr_price}
                        onChange={(e) => handlePricingChange('addon_ocr_price', e.target.value)}
                        className="border-primary/30"
                        data-testid="ocr-addon-price-input"
                      />
                      <p className="text-xs text-muted-foreground">Auto-read uploaded weight tickets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Free CWMP Toggle */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="text-foreground">Promotions</CardTitle>
                  <CardDescription>Special offers and discounts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div>
                      <p className="font-medium text-foreground">First CWMP Free</p>
                      <p className="text-sm text-muted-foreground">New users get their first CWMP report free</p>
                    </div>
                    <Switch
                      checked={pricing.free_cwmp_enabled}
                      onCheckedChange={(checked) => handlePricingChange('free_cwmp_enabled', checked)}
                      data-testid="free-cwmp-toggle"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={savePricing}
                  disabled={savingPricing}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="save-pricing-btn"
                >
                  {savingPricing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Pricing
                    </>
                  )}
                </Button>
              </div>

              {/* Pricing Summary */}
              <Card className="border-green-500/20 bg-green-500/5">
                <CardHeader>
                  <CardTitle className="text-foreground">Current Pricing Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-background rounded-lg">
                      <p className="text-2xl font-bold text-primary">${pricing.cwmp_price}</p>
                      <p className="text-sm text-muted-foreground">CWMP</p>
                    </div>
                    <div className="p-4 bg-background rounded-lg">
                      <p className="text-2xl font-bold text-primary">${pricing.final_report_price}</p>
                      <p className="text-sm text-muted-foreground">Final Report</p>
                    </div>
                    <div className="p-4 bg-background rounded-lg">
                      <p className="text-2xl font-bold text-primary">${pricing.bundle_price}</p>
                      <p className="text-sm text-muted-foreground">Bundle</p>
                    </div>
                    <div className="p-4 bg-background rounded-lg">
                      <p className="text-2xl font-bold text-primary">${pricing.subscription_price}<span className="text-sm">/mo</span></p>
                      <p className="text-sm text-muted-foreground">Subscription</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
